import ExcelJS from 'exceljs';
import { DocumentType, ProjectType, SHEETS, SHEET_BY_ID, PeopleSheet, type TemplateConfig } from '@lg/shared';
import type { SampleRow } from './samples.js';

/**
 * People and approvals workbook (spec 9.2): one worksheet per sheet, header
 * row, dropdowns for jobId / documentType / roleId, and an Instructions sheet.
 */

export interface UploadedRow {
  sheet: PeopleSheet;
  rowNumber: number;
  data: Record<string, unknown>;
}

export interface UploadResult {
  rows: UploadedRow[];
  fileErrors: string[];
}

export const MAX_ROWS_PER_SHEET = 2000;
export const MAX_FILE_BYTES = 5 * 1024 * 1024;

const LIST_SHEET = 'Lists';

export async function buildPeopleWorkbook(
  template: TemplateConfig,
  rows: readonly SampleRow[] = [],
): Promise<Uint8Array> {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Legends venue onboarding';
  addPeopleSheets(wb, template, rows);
  const buf = await wb.xlsx.writeBuffer();
  return new Uint8Array(buf as ArrayBuffer);
}

/** Adds the Instructions, hidden Lists and one worksheet per people sheet to `wb`. */
export function addPeopleSheets(
  wb: ExcelJS.Workbook,
  template: TemplateConfig,
  rows: readonly SampleRow[] = [],
): void {
  const info = wb.addWorksheet('Instructions');
  info.columns = [{ width: 24 }, { width: 90 }];
  info.addRow(['People, approvals and master data import']).font = { bold: true, size: 14 };
  info.addRow(['Template', template.id]);
  info.addRow([]);
  info.addRow([
    'How to use',
    'Fill one row per record on each sheet. Keep the header row. Columns marked * are required.',
  ]);
  info.addRow(['', 'Dropdowns list the jobs, document types and roles allowed by the template.']);
  info.addRow([
    '',
    'Approval limits: one row per job and document type; 0 means the job cannot approve that document.',
  ]);
  info.addRow([
    '',
    "Positions: the venue's top positions report to a regional or corporate position (e.g. REG-FIN-01, 000613).",
  ]);
  info.addRow([
    '',
    'Customers, vendors, project contracts and projects are optional. They are created in the new company over OData after the people import, using groups copied from the template.',
  ]);
  info.addRow([
    '',
    'Projects: TimeAndMaterial and FixedPrice projects need a contract; managers and controllers are personnel numbers from the workers sheet.',
  ]);
  info.addRow([]);
  for (const s of SHEETS) info.addRow([s.title, s.description]);
  info.protect('', {});

  const lists = wb.addWorksheet(LIST_SHEET, { state: 'hidden' });
  const jobs = template.jobCatalog.map((j) => j.jobId);
  const docs = DocumentType.options;
  const roles = template.roleCatalog.map((r) => r.roleId);
  lists.getColumn(1).values = ['jobId', ...jobs];
  lists.getColumn(2).values = ['documentType', ...docs];
  lists.getColumn(3).values = ['roleId', ...roles];
  const projectTypes = ProjectType.options;
  lists.getColumn(4).values = ['projectType', ...projectTypes];
  const listRef: Record<string, string> = {
    jobId: `${LIST_SHEET}!$A$2:$A$${jobs.length + 1}`,
    documentType: `${LIST_SHEET}!$B$2:$B$${docs.length + 1}`,
    roleId: `${LIST_SHEET}!$C$2:$C$${roles.length + 1}`,
    projectType: `${LIST_SHEET}!$D$2:$D$${projectTypes.length + 1}`,
  };

  for (const def of SHEETS) {
    const ws = wb.addWorksheet(def.sheet);
    ws.columns = def.columns.map((c) => ({
      header: c.required ? `${c.key}*` : c.key,
      key: c.key,
      width: Math.max(16, c.key.length + 6),
    }));
    ws.getRow(1).font = { bold: true };
    ws.views = [{ state: 'frozen', ySplit: 1 }];
    for (const r of rows.filter((x) => x.sheet === def.sheet)) {
      ws.addRow(
        def.columns.map((c) => {
          const v = r.data[c.key];
          if (typeof v === 'boolean') return v ? 'yes' : 'no';
          return v ?? '';
        }),
      );
    }
    def.columns.forEach((c, i) => {
      const ref = listRef[c.key];
      if (!ref) return;
      const letter = String.fromCharCode(65 + i);
      for (let row = 2; row <= 500; row++) {
        ws.getCell(`${letter}${row}`).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [ref],
          showErrorMessage: true,
          errorTitle: 'Not in the template',
          error: `Pick a ${c.label.toLowerCase()} from the list`,
        };
      }
    });
  }
}

export function cellText(value: ExcelJS.CellValue): unknown {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === 'object') {
    if ('text' in value && typeof value.text === 'string') return value.text;
    if ('richText' in value) return value.richText.map((t) => t.text).join('');
    if ('result' in value) return cellText(value.result as ExcelJS.CellValue);
    return '';
  }
  return value;
}

/** Map a header cell to a column key: accepts the key, `key*` or the label. */
function headerKey(sheet: PeopleSheet, header: string): string | null {
  const h = header.replace(/\*$/, '').trim().toLowerCase();
  const col = SHEET_BY_ID[sheet].columns.find(
    (c) => c.key.toLowerCase() === h || c.label.toLowerCase() === h,
  );
  return col?.key ?? null;
}

function rowsFromTable(sheet: PeopleSheet, table: unknown[][], errors: string[]): UploadedRow[] {
  const [header, ...body] = table;
  if (!header) return [];
  const keys = header.map((h) => headerKey(sheet, String(h ?? '')));
  const missing = SHEET_BY_ID[sheet].columns.filter((c) => c.required && !keys.includes(c.key));
  if (missing.length)
    errors.push(`Sheet ${sheet}: missing column(s) ${missing.map((c) => c.key).join(', ')}`);
  const rows: UploadedRow[] = [];
  body.forEach((cells, i) => {
    if (cells.every((c) => c === '' || c === null || c === undefined)) return;
    const data: Record<string, unknown> = {};
    keys.forEach((k, j) => {
      if (k) data[k] = cells[j] ?? '';
    });
    rows.push({ sheet, rowNumber: i + 2, data });
  });
  if (rows.length > MAX_ROWS_PER_SHEET) {
    errors.push(`Sheet ${sheet}: ${rows.length} rows; the limit is ${MAX_ROWS_PER_SHEET}`);
    return rows.slice(0, MAX_ROWS_PER_SHEET);
  }
  return rows;
}

/** Loads .xlsx bytes, or returns the reason it cannot. */
export async function loadWorkbook(bytes: Uint8Array): Promise<ExcelJS.Workbook | string> {
  if (bytes.byteLength > MAX_FILE_BYTES) return 'File is larger than 5 MB';
  const wb = new ExcelJS.Workbook();
  try {
    await wb.xlsx.load(
      bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer,
    );
  } catch {
    return 'The file is not a readable .xlsx workbook';
  }
  return wb;
}

export async function parsePeopleWorkbook(bytes: Uint8Array): Promise<UploadResult> {
  const wb = await loadWorkbook(bytes);
  if (typeof wb === 'string') return { rows: [], fileErrors: [wb] };
  const result = readPeopleSheets(wb);
  if (result.sheetsFound === 0)
    result.fileErrors.push(
      `No people sheets found; expected sheets named ${SHEETS.map((s) => s.sheet).join(', ')}`,
    );
  return { rows: result.rows, fileErrors: result.fileErrors };
}

/** Reads every worksheet named after a people sheet; other worksheets are ignored. */
export function readPeopleSheets(wb: ExcelJS.Workbook): UploadResult & { sheetsFound: number } {
  const fileErrors: string[] = [];
  const rows: UploadedRow[] = [];
  let found = 0;
  wb.eachSheet((ws) => {
    const sheet = PeopleSheet.safeParse(ws.name);
    if (!sheet.success) return;
    found += 1;
    const table: unknown[][] = [];
    ws.eachRow({ includeEmpty: true }, (row, rowNumber) => {
      const values = Array.isArray(row.values) ? row.values.slice(1) : [];
      table[rowNumber - 1] = values.map((v) => cellText(v as ExcelJS.CellValue));
    });
    rows.push(
      ...rowsFromTable(
        sheet.data,
        Array.from(table, (r) => r ?? []),
        fileErrors,
      ),
    );
  });
  return { rows, fileErrors, sheetsFound: found };
}

/** Minimal RFC 4180 CSV parser (quoted fields, escaped quotes, CRLF). */
export function parseCsv(text: string): string[][] {
  const out: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  const src = text.startsWith('\u{FEFF}') ? text.slice(1) : text;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') {
        field += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',') {
      row.push(field);
      field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i++;
      row.push(field);
      out.push(row);
      row = [];
      field = '';
    } else field += ch;
  }
  if (field !== '' || row.length) {
    row.push(field);
    out.push(row);
  }
  return out;
}

/** One CSV per sheet; the file name says which sheet (e.g. positions.csv). */
export function parsePeopleCsv(fileName: string, text: string): UploadResult {
  const fileErrors: string[] = [];
  const base = fileName.replace(/\.csv$/i, '').toLowerCase();
  const sheet = SHEETS.find((s) => base.endsWith(s.sheet.toLowerCase()));
  if (!sheet) {
    return {
      rows: [],
      fileErrors: [
        `${fileName}: name the file after its sheet (${SHEETS.map((s) => `${s.sheet}.csv`).join(', ')})`,
      ],
    };
  }
  return { rows: rowsFromTable(sheet.sheet, parseCsv(text), fileErrors), fileErrors };
}
